import { STATUS_SUMMARY_CC_BITS, type StatusSummary, type StatusSummaryCcKind, type StatusView, type StatusViewKind } from '../net/protocol.js';

/**
 * The Korean name of each built-in kind. 표식 is the contract's display name for a mark. PROVISIONAL: 무장 해제,
 * 치유 감소, 취약, 피해 감소 and 상태 (a neutral status that is not a skill's own) are working names the
 * contract's vocabulary does not list yet.
 */
export const STATUS_KIND_NAMES: Readonly<Record<StatusViewKind, string>> = {
  dot: '지속 피해', slow: '둔화', stun: '기절', root: '속박', silence: '침묵', disarm: '무장 해제',
  heal_reduction: '치유 감소', damage_amp: '취약', damage_reduction: '피해 감소', mark: '표식', neutral: '상태',
};

export interface StatusChip {
  readonly instanceId: number;
  /** The status's name, with `×stacks` when it has more than one stack. */
  readonly label: string;
  /** Share of the lifetime left, 0..1; null when the status has no end or the tick is not known yet. */
  readonly remaining: number | null;
}

/**
 * The chips a player sees over one monster: only the statuses that player's own casts left, in wire order. A skill
 * status shows its own name, every other status its kind's name. An ended status shows nothing.
 */
export function monsterStatusChips(statuses: readonly StatusView[], localNetId: string | undefined, serverTick: number | undefined): StatusChip[] {
  if (localNetId === undefined) return [];
  const chips: StatusChip[] = [];
  for (const status of statuses) {
    if (status.ownerNetId !== localNetId) continue;
    const expire = status.expireServerTick;
    if (expire !== null && serverTick !== undefined && expire <= serverTick) continue;
    const name = status.displayName ?? STATUS_KIND_NAMES[status.kind];
    const remaining = expire === null || serverTick === undefined ? null
      : Math.max(0, Math.min(1, (expire - serverTick) / (expire - status.appliedServerTick)));
    chips.push({ instanceId: status.instanceId, label: status.definitionId === 'element:frostbite' || status.definitionId === 'element:freeze' || status.definitionId === 'element:shock' ? `${name} ${status.stacks}` : status.stacks > 1 ? `${name} ×${status.stacks}` : name, remaining });
  }
  return chips;
}

/** The Korean name of each crowd-control bit of a status summary. 빙결 is the contract's name for freeze. */
const SUMMARY_CC_NAMES: Readonly<Record<StatusSummaryCcKind, string>> = {
  stun: STATUS_KIND_NAMES.stun, root: STATUS_KIND_NAMES.root, silence: STATUS_KIND_NAMES.silence, disarm: STATUS_KIND_NAMES.disarm, frozen: '빙결',
};

export interface StatusSummaryMarks {
  /** Names of the crowd control others left, in bit order. */
  readonly cc: readonly string[];
  /** The strongest slow others left, 0..1; 0 draws no tint. */
  readonly slow: number;
}

/** What a monster shows of other players' statuses: one crowd-control row and a slow tint. Null when there is nothing to draw. */
export function statusSummaryMarks(summary: StatusSummary | null | undefined): StatusSummaryMarks | null {
  const cc = (Object.keys(STATUS_SUMMARY_CC_BITS) as StatusSummaryCcKind[])
    .filter((kind) => ((summary?.cc ?? 0) & STATUS_SUMMARY_CC_BITS[kind]) !== 0).map((kind) => SUMMARY_CC_NAMES[kind]);
  const slow = summary?.slow ?? 0;
  return cc.length === 0 && slow === 0 ? null : { cc, slow };
}
